import React, { FunctionComponent, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  LinearProgress,
  IconButton,
  Select,
  MenuItem,
  Skeleton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import classNames from 'classnames';
import { ClassNameMap } from '@mui/styles';
import { routePathMap } from 'modules/common';
import { SmartDiaryNotesSkeleton } from 'modules/smartDiary/smartDiaryNotes/SmartDiaryNotesSkeleton';
import { UnifiedCollectorListPageSkeleton } from 'modules/Collectors/UnifiedCollectorListPageSkeleton';
import { SettingsPageSkeleton } from 'modules/Settings/SettingsPageSkeleton';
import { CollectorIntermediatePageSkeleton } from 'modules/Collectors/Install/CollectorIntermediatePageSkeleton';
import CollectorErrorPageSkeleton from 'modules/Collectors/ErrorPage/CollectorErrorPageSkeleton';
import CollectorAccessDeniedSkeleton from 'modules/Collectors/ErrorPage/CollectorAccessDeniedSkeleton';
import CollectorInsufficientPermissionsSkeleton from 'modules/Collectors/ErrorPage/CollectorInsufficientPermissionsSkeleton';
import { GenericCircularProgess } from 'modules/App/menu/items/Settings/GenericCircularProgess';
import { useFeatureFlag } from 'modules/common/hooks';
import { SkeletonRounded } from './SkeletonRounded';
import { NotesListSkeleton } from './NotesListSkeleton';

const useStyles = makeStyles((theme) => ({
  container: {
    background: 'inherit',
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  },
  avatar: {
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    top: theme.spacing(2.5),
    marginRight: theme.spacing(2),
    alignItems: 'center'
  },
  page: {
    background: theme.palette.background.default,
    height: '100vh',
    boxShadow: theme.shadow?.paper,
    display: 'flex',
    width: '100%'
  },
  smartDiary: {
    display: 'flex'
  },
  menu: {
    display: 'flex',
    marginLeft: theme.spacing(4.5),
    marginTop: theme.spacing(-1.5)
  },
  menu1: {
    width: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.spacing(6),
    background: theme.palette.background.default,
    marginRight: theme.spacing(0.5),
    borderRadius: theme.spacing(1, 1, 0, 0),
    boxShadow: theme.shadow?.menu
  },
  sidebar: {
    width: theme.spacing(8),
    padding: theme.spacing(1),
    borderRight: '1.5px solid rgb(219, 219, 219)'
  },
  addNote: {
    width: 50,
    height: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(5, 5, 5, 5),
    backgroundColor: theme.palette.grey[200],
    boxShadow: theme.shadow?.menu
  },
  collector: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  collectorContainer: {
    width: '100%'
  },
  logoContainer: {
    width: 50,
    height: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    boxShadow: theme.shadow?.menu
  },
  settingContainer: {
    width: '100%'
  },
  setting: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  insightContainer: {
    width: '100%'
  },
  insight: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  iconButton: {
    color: theme.palette.grey[300]
  },
  iconButtonBack: {
    height: 20
  },
  iconButtonForward: {
    height: 20
  },
  insightRightNavItem: {
    marginRight: 60,
    marginLeft: 'auto'
  },
  header: {
    display: 'flex',
    width: '30%',
    marginLeft: theme.spacing(1.75)
  },
  headerTab: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: theme.spacing(40)
  },
  selectorInsight: {
    margin: theme.spacing(1, 0, 1, 2),
    height: theme.spacing(5),
    width: '8.5%'
  },
  menuItem: {
    display: 'flex',
    background: theme.skelton?.color,
    width: '100%',
    height: theme.spacing(3.75),
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(1)
  }
}));

type PageLoadingProps = {
  classes: ClassNameMap;
};
const SmartDiaryPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classNames(classes.page, classes.smartDiary)}>
      <Box width={'38%'}>
        <SmartDiaryNotesSkeleton />
      </Box>
      <Box>
        <LinearProgress />
      </Box>
    </Box>
  );
};

const CollectorsPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classes.page}>
      <Box className={classes.collectorContainer}>
        <Box className={classes.collector}>
          <Box className={classes.header}>
            <Box className={classes.headerTab}>
              <SkeletonRounded width={'35%'} height={38} />
              <SkeletonRounded width={'35%'} height={38} />
            </Box>
          </Box>
        </Box>
        <Box marginTop={1}>
          <SkeletonRounded width={'140%'} height={2} />
        </Box>
        <Box>
          <UnifiedCollectorListPageSkeleton />
        </Box>
      </Box>
    </Box>
  );
};

const SettingsPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classes.page}>
      <Box className={classes.settingContainer}>
        <Box className={classes.setting}>
          <Box className={classes.header}>
            <Box className={classes.headerTab}>
              <SkeletonRounded width={'35%'} height={38} />
              <SkeletonRounded width={'35%'} height={38} />
            </Box>
          </Box>
        </Box>
        <Box marginTop={1}>
          <SkeletonRounded width={'140%'} height={2} />
        </Box>
        <Box>
          <SettingsPageSkeleton />
        </Box>
      </Box>
    </Box>
  );
};

const InsightsPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classes.page}>
      <Box className={classes.insightContainer}>
        <Box className={classes.insight}>
          <IconButton className={classes.iconButton} disabled>
            <ChevronLeftIcon className={classes.iconButtonBack} />
          </IconButton>
          <IconButton className={classes.iconButton} disabled>
            <ChevronRightIcon className={classes.iconButtonForward} />
          </IconButton>
          <SkeletonRounded width={'18.5%'} height={30} />
          <Select
            displayEmpty
            data-testid="selector"
            className={classes.selectorInsight}
          >
            <MenuItem>
              <Skeleton className={classes.menuItem}></Skeleton>
            </MenuItem>
          </Select>
          <Box className={classes.insightRightNavItem}>
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>
        <Box marginTop={1}>
          <SkeletonRounded width={'140%'} height={2} />
        </Box>
      </Box>
    </Box>
  );
};

const MyWorkPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classes.page}>
      <NotesListSkeleton />
    </Box>
  );
};

const GenericPageLoading: React.FC<PageLoadingProps> = ({
  classes
}: PageLoadingProps) => {
  return (
    <Box className={classes.page}>
      <GenericCircularProgess />
    </Box>
  );
};

type AppSidePanelLoadingProps = {
  children?: React.ReactNode;
};

const AppSidePanelLoading: React.FC<AppSidePanelLoadingProps> = ({
  children
}: AppSidePanelLoadingProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.page}>
      <Box className={classes.sidebar}>
        <Box className={classes.logoContainer}>
          <SkeletonRounded height={35} width={30} />
        </Box>
        <Box className={classes.addNote}>
          <SkeletonRounded height={35} width={30} />
        </Box>
        <Box>
          {Array.from(new Array(5)).map((item, index) => (
            <Box marginTop={-1} key={index}>
              <SkeletonRounded height={40} />
            </Box>
          ))}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

const getContent = ({
  tabName,
  classes,
  isTiAutoAcceptUserSettingsEnabled
}: {
  tabName: string;
  classes: ClassNameMap;
  isTiAutoAcceptUserSettingsEnabled: boolean;
}): React.ReactElement => {
  const tabNameWithRemovedEndSlash =
    tabName[tabName.length - 1] === '/' ? tabName.slice(0, -1) : tabName;

  switch (tabNameWithRemovedEndSlash) {
    case routePathMap.notes:
      return <MyWorkPageLoading classes={classes} />;
    case routePathMap.smartDiary:
      return <SmartDiaryPageLoading classes={classes} />;
    case routePathMap.collectors:
      return <CollectorsPageLoading classes={classes} />;
    case routePathMap.settings:
      return <SettingsPageLoading classes={classes} />;
    case routePathMap.insights:
      return <InsightsPageLoading classes={classes} />;
    default:
      return isTiAutoAcceptUserSettingsEnabled ? (
        <GenericPageLoading classes={classes} />
      ) : (
        <MyWorkPageLoading classes={classes} />
      );
  }
};

const Loading: FunctionComponent = () => {
  const classes = useStyles();
  const tabName = window.location.pathname;
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );
  const contentPage = useMemo(
    () => getContent({ tabName, classes, isTiAutoAcceptUserSettingsEnabled }),
    [tabName, classes, isTiAutoAcceptUserSettingsEnabled]
  );

  if (tabName.startsWith('/collector/error')) {
    return <CollectorErrorPageSkeleton />;
  }
  if (tabName.startsWith('/collector/access_denied')) {
    return <CollectorAccessDeniedSkeleton />;
  }
  if (tabName.startsWith('/collector/insufficient_permissions')) {
    return <CollectorInsufficientPermissionsSkeleton />;
  }

  if (tabName.startsWith('/collector/')) {
    return <CollectorIntermediatePageSkeleton />;
  }

  if (tabName.startsWith('/download')) {
    return <></>;
  }

  return (
    <Box className={classes.container}>
      <AppSidePanelLoading>{contentPage}</AppSidePanelLoading>
    </Box>
  );
};

export default Loading;
